"use client";
import * as React from "react";
import { useState, useEffect } from "react";
import { MapPin, Clock, Users, ChevronRight, RefreshCw, AlertCircle } from "lucide-react";
import ApplicationForm from "./ApplicationForm";

const JobCard = ({ job, onApply, delay = 0 }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <article
      className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 hover:bg-white/15 transition-all duration-300 group"
      style={{
        animationDelay: `${delay}ms`,
        animation: "slideIn 0.8s ease-out forwards",
      }}
      role="article"
      aria-labelledby={`job-${job.id}`}
    >
      <div className="flex flex-col space-y-2">
        {/* Job Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3
              id={`job-${job.id}`}
              className="text-xl font-bold text-white mb-2 group-hover:text-[#C9C51A] transition-colors duration-300 font-heading leading-tight lg:text-xl md:text-lg sm:text-base"
            >
              {job.title}
            </h3>
            <div className="flex flex-col space-y-2 text-sm text-white/70 mb-3 sm:flex-row sm:space-y-0 sm:space-x-4">
              <div className="flex items-center space-x-1">
                <MapPin className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                <span className="text-xs lg:text-sm md:text-xs sm:text-xs">{job.location}</span>
              </div>
              <div className="flex items-center space-x-4">
                {/* <div className="flex items-center space-x-1">
                  <Clock className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs lg:text-sm md:text-xs sm:text-xs">Full-time</span>
                </div> */}
                <div className="flex items-center space-x-1">
                  <Users className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                  <span className="text-xs lg:text-sm md:text-xs sm:text-xs">{job.department}</span>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300 flex-shrink-0"
            aria-label={isExpanded ? "Collapse job details" : "Expand job details"}
            aria-expanded={isExpanded}
          >
            <ChevronRight
              className={`w-5 h-5 text-white transition-transform duration-300 ${
                isExpanded ? 'rotate-90' : ''
              }`}
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Job Summary */}
        <p className="text-white/80 leading-relaxed text-sm font-body lg:text-sm md:text-xs sm:text-xs">
          {job.summary}
        </p>

        {/* Expandable Content */}
        <div className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="pt-4 border-t border-white/20">
            <h4 className="font-semibold text-white mb-3 text-sm">Key Responsibilities:</h4>
            <div className="text-white/70 text-xs leading-relaxed mb-4">
              {job.responsibilities}
            </div>
            {job.requirements && (
              <h4 className="font-semibold text-white mb-3 text-sm">Requirements:</h4>
            )}
            <p className="text-white/70 text-xs leading-relaxed">
              {job.requirements}
            </p>
          </div>
        </div>

        {/* Apply Button */}
        <div className="flex justify-end pt-1">
          <button
            onClick={() => onApply(job)}
            className="px-6 py-2 bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] text-white font-semibold rounded-full hover:from-[#C9C51A] hover:to-[#D96F2B] transition-all duration-300 transform hover:scale-105 shadow-lg text-sm lg:px-6 lg:py-2 md:px-4 md:py-1.5 sm:px-4 sm:py-1.5 lg:text-sm md:text-xs sm:text-xs"
          >
            Apply Now
          </button>
        </div>
      </div>
    </article>
  );
};

function CareersJobs() {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Google Apps Script web app URL - same as contact form
  const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbx78Y8WRIyWyufP0Awn6hFMI6GXVVhPotIncuIPjirPvQmlayE4xlndrXTrauT6hHKT/exec";

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {

  setIsLoading(true);

  setError(null);

  try {

    const response = await fetch(

      `${GOOGLE_SCRIPT_URL}?action=getJobs&t=${Date.now()}`,

      {

        method: "GET",

        redirect: "follow",

      }

    );

    if (!response.ok) {

      throw new Error(`HTTP ${response.status}`);

    }

    const data = await response.json();

    console.log("Jobs data:", data);

    if (data.success && Array.isArray(data.jobs)) {

      setJobs(data.jobs);

    } else {

      throw new Error(data.message || "Invalid response");

    }

  } catch (err) {

    console.error("Fetch jobs failed:", err);

    setJobs(getFallbackJobs());

    setError(err.message);

  } finally {

    setIsLoading(false);

  }

};


  const getFallbackJobs = () => {
    return [
      {
        id: 1,
        title: "Cloud Storage & Automation Engineer",
        location: "Cumming, Georgia, 30041, USA",
        department: "Cloud Engineering",
        summary: "Join our cloud engineering team to identify new business requirements and automate tasks using cutting-edge technologies.",
        responsibilities: "Identify the new business requirements in the area of cloud and automate the tasks using Ansible & Python, assert deployment and initial configuration of various devices like EMC Isilon, PMAX, XtremIO, VNX (file & block), NetApp OnTap cluster, Cisco MDS SAN switches, virtual CVO, storage & compute in Azure and GCP clouds. Manage existing on-premise and cloud infrastructure and make sure the system services are available without any outage, snapmirror, robocopy, EMcopy, SyncIQ, Azure File Sync, etc., SSH/RDP to a system, daily system health checks, report generations, etc. using Ansible, Python & PowerShell.",
        requirements: "Bachelor Degree In Technology or Computer Science or Related Field with Relevant Industry Experiences"
      },
      {
        id: 2,
        title: "UI Developer",
        location: "Cumming, Georgia, 30041, USA",
        department: "Frontend Development",
        summary: "Create intuitive and user-friendly software using modern web technologies and frameworks.",
        responsibilities: "Create intuitive and user-friendly software using HTML5, CSS3, Angular.JS, Bootstrap and Java, Use CXF on top of Spring to develop the web services and connect to the databases, Create maintainable and testable single-page applications, use Angular 6 and implement 2-way Data binding and HTTP calls to fetch data from backend servers, also use Angular 6 Router to enable navigation and implement CSS3 media queries to ensure the application is responsive and fits different screen sizes, Use Jenkins for continuing integration and continuous deployment pipeline, and Bitbucket as the code repository and GIT for version control.",
        requirements: "Bachelor Degree In Technology or Computer Science or Related Field with Relevant Industry Experiences"
      },
      {
        id: 3,
        title: "Cloud Data Administrator",
        location: "Cumming, Georgia, 30041, USA",
        department: "Database Administration",
        summary: "Provide comprehensive database solutions and cloud migration services for enterprise clients.",
        responsibilities: "Provide solutions like Replication, Log Shipping, Database Mirroring, SQL Clustering and Always On Availability, performance tuning, Configure Azure Services PaaS, IaaS and Migrated databases from on premises to Azure, Install and configure of MS SQL Server 2005, 2008, 2008R2, 2012, 2014, 2016, and applying service packs hotfixes, Database Administration activities such as SQL Server Maintenance, Database Maintenance, Disk Space management, Security Issues, Database Backup and Recovery, Implement of HA/DR solutions like Log Shipping, Database Mirroring, SQL Clustering, and Always On Availability, Initial troubleshoot of replication problems and migrate of SQL servers from lower to higher version, Configure of Azure Services PaaS, IaaS, and migration of databases from on-premises to Azure, create and monitor Azure SQL Server databases, configuring and maintaining Elastic DB pools, and monitoring Geo Replication. EC2, RDS, S3, CloudWatch, EBS resources to communicate with each other using IAM, AWS services like SMS, DBMS, Cloud Formation, S3, Route53 Glacier, EC2, RDS, and VPC, Web applications and databases, Cloud Computing AWS EC2, RDS, S3, Glacier, SQS, SNS, SES, CloudFormation, VPC, IAM, Route53, & EBS.",
        requirements: "Bachelor Degree In Technology or Computer Science or Related Field with Relevant Industry Experiences"
      }
    ];
  };

  const handleApply = (job) => {
    setSelectedJob(job);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedJob(null);
  };

  const handleRetry = () => {
    setRetryCount(0);
    fetchJobs();
  };

  return (
    <>
      <section
        id="job-listings"
        className="relative py-0 overflow-hidden"
        aria-labelledby="jobs-heading"
      >
        <style >{`
          @keyframes slideIn {
            0% {
              opacity: 0;
              transform: translateX(20px);
            }
            100% {
              opacity: 1;
              transform: translateX(0px);
            }
          }
          @keyframes dotFade {
            0%,
            100% {
              opacity: 0.6;
            }
            50% {
              opacity: 0.9;
            }
          }
          @keyframes spin {
            0% {
              transform: rotate(0deg);
            }
            100% {
              transform: rotate(360deg);
            }
          }
          .dot {
            width: 4px;
            height: 4px;
            background: rgba(255, 255, 255, 0.8);
            border-radius: 50%;
            animation: dotFade 3s ease-in-out infinite;
            box-shadow: 0 0 4px rgba(255, 255, 255, 0.6);
          }
          .dot-pattern-tl {
            position: absolute;
            top: 2rem;
            left: 2rem;
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            gap: 8px;
            z-index: 10;
          }
          .dot-pattern-br {
            position: absolute;
            bottom: 2rem;
            right: 2rem;
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            gap: 8px;
            z-index: 10;
          }
          .dot-pattern-tl .dot:nth-child(n + 1):nth-child(-n + 8) {
            animation-delay: 0s;
            opacity: 0.9;
          }
          .dot-pattern-tl .dot:nth-child(n + 9):nth-child(-n + 16) {
            animation-delay: 0.2s;
            opacity: 0.8;
          }
          .dot-pattern-tl .dot:nth-child(n + 17):nth-child(-n + 24) {
            animation-delay: 0.4s;
            opacity: 0.7;
          }
          .dot-pattern-tl .dot:nth-child(n + 25):nth-child(-n + 32) {
            animation-delay: 0.6s;
            opacity: 0.6;
          }
          .dot-pattern-tl .dot:nth-child(n + 33):nth-child(-n + 40) {
            animation-delay: 0.8s;
            opacity: 0.5;
          }
          .dot-pattern-tl .dot:nth-child(n + 41):nth-child(-n + 48) {
            animation-delay: 1s;
            opacity: 0.4;
          }
          .dot-pattern-tl .dot:nth-child(n + 49):nth-child(-n + 56) {
            animation-delay: 1.2s;
            opacity: 0.3;
          }
          .dot-pattern-tl .dot:nth-child(n + 57):nth-child(-n + 64) {
            animation-delay: 1.4s;
            opacity: 0.2;
          }

          .dot-pattern-br .dot:nth-child(n + 1):nth-child(-n + 8) {
            animation-delay: 1.4s;
            opacity: 0.2;
          }
          .dot-pattern-br .dot:nth-child(n + 9):nth-child(-n + 16) {
            animation-delay: 1.2s;
            opacity: 0.3;
          }
          .dot-pattern-br .dot:nth-child(n + 17):nth-child(-n + 24) {
            animation-delay: 1s;
            opacity: 0.4;
          }
          .dot-pattern-br .dot:nth-child(n + 25):nth-child(-n + 32) {
            animation-delay: 0.8s;
            opacity: 0.5;
          }
          .dot-pattern-br .dot:nth-child(n + 33):nth-child(-n + 40) {
            animation-delay: 0.6s;
            opacity: 0.6;
          }
          .dot-pattern-br .dot:nth-child(n + 41):nth-child(-n + 48) {
            animation-delay: 0.4s;
            opacity: 0.7;
          }
          .dot-pattern-br .dot:nth-child(n + 49):nth-child(-n + 56) {
            animation-delay: 0.2s;
            opacity: 0.8;
          }
          .dot-pattern-br .dot:nth-child(n + 57):nth-child(-n + 64) {
            animation-delay: 0s;
            opacity: 0.9;
          }

          .loading-spinner {
            animation: spin 1s linear infinite;
          }

          @media (max-width: 768px) {
            .dot-pattern-tl {
              display: none;
            }
            .dot-pattern-br {
              bottom: 1rem;
              right: 1rem;
              grid-template-columns: repeat(6, 1fr);
              gap: 6px;
            }
            .dot {
              width: 3px;
              height: 3px;
            }
          }
        `}</style>

        {/* Main Container Box */}
        <div className="mx-2 lg:mx-6 xl:mx-8 md:mx-4 sm:mx-2 mb-4 bg-gradient-to-br from-black to-[#1e3a8a] rounded-3xl border border-white/20 backdrop-blur-sm relative">
          <div className="relative overflow-hidden">
            {/* Background Gradient */}
            <div className="absolute inset-0" aria-hidden="true" />

            {/* Accent Glow Effects */}
            <div
              className="absolute top-10 left-1/2 transform -translate-x-1/2 w-96 h-32 rounded-full blur-3xl opacity-20 lg:opacity-20 md:opacity-15 sm:opacity-5 lg:w-96 lg:h-32 md:w-72 md:h-24 sm:w-48 sm:h-16"
              style={{
                backgroundColor: "#D96F2B",
              }}
              aria-hidden="true"
            />

            <div
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2 w-80 h-40 rounded-full blur-3xl opacity-15 lg:w-80 lg:h-40 md:w-60 md:h-30 sm:w-40 sm:h-20"
              style={{
                backgroundColor: "#C9C51A",
              }}
              aria-hidden="true"
            />

            {/* Decorative Dots - Top Left */}
            <div className="hidden md:block dot-pattern-tl" aria-hidden="true">
              {Array.from({ length: 64 }).map((_, i) => (
                <div key={`tl-${i}`} className="dot" />
              ))}
            </div>

            {/* Decorative Dots - Bottom Right */}
            <div className="dot-pattern-br" aria-hidden="true">
              {Array.from({ length: 64 }).map((_, i) => (
                <div key={`br-${i}`} className="dot" />
              ))}
            </div>

            {/* Content Container */}
            <div className="container mx-auto px-4 py-12 sm:py-8 md:py-8 lg:py-12 relative z-20">
              <div className={`
                transition-all duration-1000 ease-out
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
              `}>

                {/* Header Section */}
                <div className="text-center mb-8 lg:mb-8 md:mb-6 sm:mb-4">
                  <div className="inline-flex items-center px-6 py-2 bg-white/80 backdrop-blur-md border border-white/60 rounded-full mb-2 shadow-lg">
                    <span className="w-2 h-2 bg-[#1e3a8a] rounded-full mr-3 animate-pulse" aria-hidden="true"></span>
                    <span style={{ color: '#1e3a8a' }} className="text-xs md:text-sm font-semibold uppercase tracking-wider">
                      Open Positions
                    </span>
                  </div>

                  <header>
                    <h1 id="jobs-heading" className="text-4xl xl:text-6xl lg:text-5xl md:text-4xl sm:text-2xl font-bold text-white leading-tight font-heading mb-6 lg:mb-6 md:mb-4 sm:mb-3">
                      Current Job Openings <br/>
                      <span className="bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] bg-clip-text text-transparent">
                        Join Our Team
                      </span>
                    </h1>
                    <p className="text-md lg:text-xl md:text-lg sm:text-sm text-white/80 leading-relaxed font-body max-w-3xl mx-auto lg:max-w-3xl md:max-w-2xl sm:max-w-xl">
                      Discover exciting career opportunities and join our team of talented professionals
                      working on cutting-edge technology solutions.
                    </p>
                  </header>
                </div>

                {/* Loading State */}
                {isLoading && (
                  <div className="flex flex-col items-center justify-center py-16">
                    <div className="loading-spinner w-12 h-12 border-4 border-white/20 border-t-[#C9C51A] rounded-full mb-4"></div>
                    <p className="text-white/80 text-lg">Loading job opportunities...</p>
                  </div>
                )}

                {/* Error State - When we have fallback jobs */}
                {error && !isLoading && jobs.length > 0 && (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-2xl p-6 text-center mb-8">
                    <AlertCircle className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-yellow-300 mb-2">Showing Cached Positions</h3>
                    <p className="text-yellow-200 text-sm mb-3">
                      Unable to load the latest job listings. Showing available positions from our cache.
                    </p>
                    <button
                      onClick={handleRetry}
                      className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold rounded-full transition-colors duration-300 flex items-center gap-2 mx-auto text-sm"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Try Loading Latest Jobs
                    </button>
                  </div>
                )}

                {/* Error State - When no jobs at all */}
                {error && !isLoading && jobs.length === 0 && (
                  <div className="bg-red-500/20 border border-red-500/30 rounded-2xl p-8 text-center mb-8">
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-red-300 mb-2">Unable to Load Jobs</h3>
                    <p className="text-red-200 mb-4">
                      We're having trouble loading job openings. Please try again or contact us directly.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <button
                        onClick={handleRetry}
                        className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-full transition-colors duration-300 flex items-center gap-2 mx-auto"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Retry
                      </button>
                      <a
                        href="mailto:career@vastainfo.com"
                        className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-full transition-colors duration-300 inline-block text-center"
                      >
                        Contact Us Directly
                      </a>
                    </div>
                  </div>
                )}

                {/* Jobs Grid */}
                {!isLoading && jobs.length > 0 && (
                  <div className="grid gap-6 lg:gap-8 md:gap-6 sm:gap-4 mb-12 lg:mb-16 md:mb-12 sm:mb-8" role="list" aria-label="Available job positions">
                    {jobs.map((job, index) => (
                      <div key={job.id} role="listitem">
                        <JobCard
                          job={job}
                          onApply={handleApply}
                          delay={index * 100}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* No Jobs State */}
                {!isLoading && !error && jobs.length === 0 && (
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 text-center mb-8">
                    <h3 className="text-2xl font-bold text-white mb-4">No Open Positions</h3>
                    <p className="text-white/80 text-lg mb-6">
                      We don't have any open positions at the moment, but we're always looking for talented individuals.
                    </p>
                    <a
                      href="mailto:career@vastainfo.com"
                        className="px-8 py-3 bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] text-white font-semibold rounded-full hover:from-[#C9C51A] hover:to-[#D96F2B] transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black inline-block"
                    >
                      Send Your Resume
                    </a>
                  </div>
                )}

                {/* Contact Section */}
                {!isLoading && (
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 lg:p-12 text-white text-center">
                    <h2 className="text-3xl font-bold mb-6 text-white font-heading">Don't See the Right Position?</h2>
                    <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto font-body">
                      We're always looking for talented individuals to join our team. Send us your resume
                      and let us know how you can contribute to our mission.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                      <a
                        href="mailto:career@vastainfo.com"
                        className="px-8 py-3 bg-gradient-to-r from-[#D96F2B] to-[#C9C51A] text-white font-semibold rounded-full hover:from-[#C9C51A] hover:to-[#D96F2B] transition-all duration-300 transform hover:scale-105 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black"
                        aria-label="Send your resume to career@vastainfo.com"
                      >
                        Send Your Resume
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form Modal */}
      {isFormOpen && selectedJob && (
        <ApplicationForm
          job={selectedJob}
          onClose={handleCloseForm}
        />
      )}
    </>
  );
}

export default CareersJobs;
